import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { LocaleContext } from "../../../routing/LangRouter";
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";
import { toast } from "react-toastify";
import { saveHotelSelectedFilters } from "../../../store/actions";
import useRoomSelection from "../../../hooks/useRoomSelection";

//import utils
import { numberWithCommas } from "../../../utils/utility";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints";

//import general components
import { BaseButton } from "../../common/base-button/base-button";

//import images and icons
import { AddSquare, MinusSquare } from "iconsax-react";

//import styles
import "./hotel-room-item.scss";

function HotelRoomItem({ dispatch, hotelDetails, dateLength }) {
    //general variables
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const navigate = useNavigate();

    //sessionStorage variables
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation"));

    //room state management
    const [availableRooms, setAvailableRooms] = useState([]);
    const [halfPayRule, setHalfPayRule] = useState(null);
    useEffect(() => {
        if (!hotelDetails) return;

        const initializeRooms = hotelRooms => {
            return hotelRooms?.map(({ rate_plans, ...roomDetails }) => ({
                ...roomDetails,
                rate_plans: rate_plans?.map(plan => ({ ...plan, count: 0 })),
            }));
        };

        setAvailableRooms(initializeRooms(hotelDetails.rooms || []));
        setHalfPayRule(hotelDetails?.half_pay_rule);
    }, [hotelDetails]);

    //render room counter
    const renderRoomCounter = (room, ratePlan, roomIndex, ratePlanIndex) => (
        <Grid container item xs="auto" columnGap={2} justifyContent="space-evenly" alignItems="center">
            <Grid container item xs="auto" order={locale === "en" ? 3 : 1}>
                <AddSquare
                    size={IS_MOBILE ? "30" : "20"}
                    color="var(--primary-color)"
                    cursor="pointer"
                    onClick={() => handleRoomSelection("add", room, ratePlan, roomIndex, ratePlanIndex)}
                />
            </Grid>
            <Grid container item xs="auto" order={2}>
                <span>{getSelectedRoomCount(room.room_type_id, ratePlan.id)}</span>
            </Grid>
            <Grid container item xs="auto" order={locale === "en" ? 1 : 3}>
                {renderMinusButton(room, ratePlan, roomIndex, ratePlanIndex)}
            </Grid>
        </Grid>
    );

    //get selected room count
    const getSelectedRoomCount = (roomId, ratePlanId) => {
        const selectedRoom = selectedRooms.find(item => item.planId === ratePlanId && item.roomId === roomId);
        return selectedRoom ? selectedRoom.count : 0;
    };

    //render minus button
    const renderMinusButton = (room, ratePlan, roomIndex, ratePlanIndex) => {
        const isSelected = selectedRooms.some(item => item.planId === ratePlan.id && item.roomId === room.room_type_id);
        return isSelected ? (
            <MinusSquare
                size={IS_MOBILE ? "30" : "20"}
                color="var(--primary-color)"
                cursor="pointer"
                onClick={() => handleRoomSelection("remove", room, ratePlan, roomIndex, ratePlanIndex)}
            />
        ) : (
            <MinusSquare size={IS_MOBILE ? "30" : "20"} color="var(--gray-color)" />
        );
    };

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveHotelSelectedFilters(null));
    };

    //room selection management
    const { selectedRooms, handleRoomSelection } = useRoomSelection(availableRooms, halfPayRule, setAvailableRooms);

    //save selected rooms
    const saveSelectedRooms = () => {
        const count = selectedRooms.reduce((acc, item) => acc + item.count, 0);
        if (count > 4) return toast.warning(t("Hotel.Room Selection Limit"), { toastId: "custom-id-yes" });

        if (!selectedRooms.length) {
            toast.error(t("hotelMessage3"), { toastId: "custom-id-yes" });
            return;
        }

        sessionStorage.setItem("selectedRooms", JSON.stringify(selectedRooms));
        resetFilters();

        if (generalInformation?.isItinerary) {
            updateItineraryGenerate();
            updateRoutes();
            navigate(`/${locale}/itinerary/itinerary-page`);
        } else {
            navigate(`/${locale}/hotel-passengers`);
        }
    };

    //update the layout routes
    const updateRoutes = () => {
        const newRoute = `/${locale}/itinerary/itinerary-page`;
        setRoutes([...routes, newRoute]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, newRoute]));
    };

    //update the itinerary generate to true
    const updateItineraryGenerate = () => {
        itineraryInformation.itineraryGenerate = true;
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    return (
        <Grid container item xs={12} rowGap={2}>
            
            {availableRooms?.map((room, roomIndex) => {
                return (
                    <>
                        {room?.rate_plans?.map((ratePlan, ratePlanIndex) => {
                            return (
                                <Grid className="hotel-room-item__room-item" container item xs={12} rowGap={2}>
                                    <Grid container item xs={12} className="hotel-room-item__room-details" rowGap={1}>
                                        <Grid container item xs={12} justifyContent="space-between" alignItems="center">
                                            <Grid
                                                className="hotel-room-item__room-name"
                                                component="span"
                                                container
                                                item
                                                xs={8}
                                                columnGap={1}
                                                alignItems="center"
                                            >
                                                {room?.room_type_name}
                                                <span className="hotel-room-item__type">({ratePlan?.name})</span>
                                            </Grid>
                                            <Grid className="hotel-room-item__capacity" component="span" container item xs="auto">
                                                {room?.room_type_capacity + ` ${t("person")}`}
                                            </Grid>
                                        </Grid>

                                        <Grid container item xs={12} columnGap={1}>
                                            <span className={`hotel-room-item__features ${ratePlan?.prices?.accept_child ? "active" : ""}`}>
                                                {t("acceptChild")}
                                            </span>
                                            <span className={`hotel-room-item__features ${ratePlan?.cancelable ? "active" : ""}`}>{t("cancelable")}</span>
                                            <span className={`hotel-room-item__features ${room?.room_type_extra_capacity > 0 ? "active" : ""}`}>
                                                {room?.room_type_extra_capacity === 0
                                                    ? t("noExtraCapacity")
                                                    : room?.room_type_extra_capacity + t("extraCapacity")}
                                            </span>
                                        </Grid>

                                        <Grid container item xs={12} justifyContent="space-between">
                                            <Grid className="hotel-room-item__room-name" component="span" container item xs="auto" alignItems="center">
                                                {t("roomCounter")}:
                                            </Grid>
                                            {renderRoomCounter(room, ratePlan, roomIndex, ratePlanIndex)}
                                        </Grid>
                                    </Grid>

                                    <Grid container item xs={12} justifyContent="space-between">
                                        <Grid className="hotel-room-item__room-name" component="span" container item xs="auto" alignItems="center">
                                            {`${dateLength} ${t("nightsStay")}`}:
                                        </Grid>
                                        <Grid className="hotel-room-item__price" container item xs="auto" direction="column" alignItems="center">
                                            <span className="hotel-room-item__offer-price">
                                                {numberWithCommas(ratePlan?.prices?.total_price)} {t("currency")}
                                            </span>
                                            {ratePlan?.prices?.has_off && (
                                                <>
                                                    <span className="hotel-room-item__origin-price">
                                                        {numberWithCommas(ratePlan.prices.total_board_price)} {t("currency")}
                                                    </span>
                                                    <span className="hotel-room-item__percent">
                                                        <span>
                                                            {Math.round(
                                                                ((ratePlan.prices.total_board_price - ratePlan.prices.total_price) /
                                                                    ratePlan.prices.total_board_price) *
                                                                    100,
                                                            )}
                                                            %
                                                        </span>
                                                    </span>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </>
                );
            })}

            <Grid className="hotel-room-item__room-select" container item xs={12} md={3} xl={2} px={{ xs: 2, md: 0 }}>
                <BaseButton
                    text={t("select")}
                    color="primary"
                    size="medium"
                    radius="radius-10"
                    handleClick={saveSelectedRooms}
                    disabled={!selectedRooms.length}
                />
            </Grid>
        </Grid>
    );
}

export default connect()(HotelRoomItem);
